import type { RenderRequest, RenderResult } from './types';
import type { ReactNode } from 'react';
import { createStaticRouter, createStaticHandler } from 'react-router';
import type { RouteObject, StaticHandlerContext } from 'react-router';
import { wrapStaticRouter } from './internal/WrapAppElement';
import { serializeHeadCollector } from './internal/UnirendHead';
import type { HeadCollector } from './internal/UnirendHead';
import { renderToString } from 'react-dom/server';

// Debug flag to enable/disable logging in the base renderer
const DEBUG_BASE_RENDER = false; // Set to false in a production release

/**
 * Options for base rendering, simplified API
 */
export type BaseRenderOptions = {
  /**
   * Whether to wrap the app element with React.StrictMode
   * @default true
   */
  strictMode?: boolean;
  /**
   * Optional custom wrapper component for additional providers
   * Applied after UnirendHeadProvider but before StrictMode (StrictMode is always outermost)
   * Must be a React component that accepts children
   */
  rootProviders?: React.ComponentType<{ children: ReactNode }>;
};

/**
 * Base render function that handles React Router wrapping and rendering.
 *
 * This function takes routes and handles all the router creation and wrapping logic
 * internally, including UnirendHead collection for SSR/SSG scenarios.
 *
 * @param renderRequest - The render request containing type, URL, and other options
 * @param routes - The React Router routes configuration
 * @param options - Optional configuration for rendering behavior
 * @returns RenderResult with the rendered HTML and metadata
 *
 * @example
 * ```typescript
 * // Basic usage
 * const result = unirendBaseRender({ type: "ssg", url: "/" }, routes);
 *
 * // With custom providers
 * const RootProviders = ({ children }) => <ThemeProvider>{children}</ThemeProvider>;
 * const result = unirendBaseRender(
 *   { type: "ssg", url: "/about" },
 *   routes,
 *   { rootProviders: RootProviders }
 * );
 *
 * // Without StrictMode
 * const result = unirendBaseRender(
 *   { type: "ssr", url: "/contact" },
 *   routes,
 *   { strictMode: false }
 * );
 * ```
 */
export async function unirendBaseRender(
  renderRequest: RenderRequest,
  routes: RouteObject[],
  options: BaseRenderOptions = {},
): Promise<RenderResult> {
  // Create new instances per request for isolation
  const headCollector: HeadCollector = {
    title: '',
    metas: [],
    links: [],
    htmlAttrs: {},
    bodyAttrs: {},
  };

  // Create a Static Handler
  // The handler examines the routes and prepares data for rendering
  const handler = createStaticHandler(routes);

  // Pass the Fetch Request and Query the Handler
  // Pass the request to the handler to get a rendering context
  let context: StaticHandlerContext | Response;

  try {
    context = await handler.query(renderRequest.fetchRequest);
  } catch (error) {
    if (DEBUG_BASE_RENDER) {
      // eslint-disable-next-line no-console
      console.error('Error querying static handler:', error);
    }

    // Return error result instead of generic 500 response
    return {
      resultType: 'render-error',
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  // Handle Redirects and Other Responses
  // If the handler returns a Response, it's a redirect or error, pass it along
  if (context instanceof Response) {
    // Log redirects for debugging
    if (DEBUG_BASE_RENDER && context.status >= 300 && context.status < 400) {
      // eslint-disable-next-line no-console
      console.log(`Redirecting to: ${context.headers.get('Location')}`);
    }

    // Note: We return the response here. The server framework (Express)
    // needs to catch this and handle the response accordingly (e.g., res.redirect).
    return {
      resultType: 'response',
      response: context,
    };
  }

  // Ensure context is not undefined or null before proceeding
  if (!context) {
    if (DEBUG_BASE_RENDER) {
      // eslint-disable-next-line no-console
      console.error(
        'Static handler query returned undefined context for request:',
        renderRequest.fetchRequest.url,
      );
    }

    // Return a 404 response if the context is missing, which the server
    // framework will handle.
    return {
      resultType: 'response',
      response: new Response('Not Found', { status: 404 }),
    };
  }

  // 5. Create Static Router
  // Use the context from the handler to create the router instance
  const router = createStaticRouter(handler.dataRoutes, context);

  // ---> Get the status code from the context BEFORE rendering
  let statusCode = context.statusCode || 200; // Default to 200 if no error/response
  let errorDetails: Error | undefined = undefined;

  const ssOnlyData: Record<string, unknown> = {};

  // Check for __ssOnly data, extract it and remove it from the context
  for (const key in context.loaderData) {
    const data = context.loaderData[key] as Record<string, unknown> | undefined;

    if (
      data &&
      typeof data === 'object' &&
      '__ssOnly' in data &&
      data.__ssOnly
    ) {
      // Clone the __ssOnly data to avoid modifying the original
      Object.assign(ssOnlyData, structuredClone(data.__ssOnly));

      // remove __ssOnly from the data since it doesn't need to be sent to the client
      // and will not appear in the window.__staticRouterHydrationData object
      delete data.__ssOnly;
    }
  }

  // Check for React Router context.errors first (the status code will default if error boundary is hit)
  if (context.errors && statusCode === 500) {
    for (const key in context.errors) {
      const error = context.errors[key] as Error & { status?: number };

      // Extract status code if available
      if (error.status && typeof error.status === 'number') {
        statusCode = error.status;
      }

      // Use the error object directly
      errorDetails = error;

      break; // Handle the first error we find
    }
  }
  // Check if any loaders returned a status code or error following our API envelope
  else if (context?.loaderData) {
    for (const key in context.loaderData) {
      const data = context.loaderData[key] as
        | {
            status_code?: number;
            error?: {
              message?: string;
              details?: { stack?: string };
            };
          }
        | undefined;

      let hasDataInThisEntry = false;

      // Check for our API envelope status_code if a custom status code is not already set
      if (
        data?.status_code &&
        typeof data.status_code === 'number' &&
        statusCode === 200
      ) {
        statusCode = data.status_code;
        hasDataInThisEntry = true;
      }

      // Check for our API envelope error object
      if (data?.error && typeof data.error === 'object') {
        // Create Error object from the envelope
        const errorMessage =
          typeof data.error.message === 'string'
            ? data.error.message
            : 'Unknown error';

        const errorObj = new Error(errorMessage);

        // Add stack trace if available
        if (
          data.error.details &&
          typeof data.error.details === 'object' &&
          typeof data.error.details.stack === 'string'
        ) {
          errorObj.stack = data.error.details.stack;
        }

        // Mark this as an API envelope error
        (errorObj as Error & { source?: string }).source = 'api-envelope';

        errorDetails = errorObj;
        hasDataInThisEntry = true;
      }

      // If we found relevant data in this entry, we can break
      if (hasDataInThisEntry) {
        break;
      }
    }
  }

  // Render the App HTML
  const wrappedElement = wrapStaticRouter(
    router,
    context,
    {
      strictMode: options.strictMode,
      rootProviders: options.rootProviders,
      unirendContext: renderRequest.unirendContext, // unirendContext is always provided in renderRequest by SSRServer or SSG Generation
    },
    headCollector,
  );

  const appHTML = renderToString(wrappedElement);

  // Serialize collected head data AFTER rendering
  const head = serializeHeadCollector(headCollector);

  return {
    resultType: 'page',
    html: appHTML,
    head,
    statusCode: statusCode,
    errorDetails: errorDetails,
    ssOnlyData: ssOnlyData,
  };
}
